import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { annonceService, sousServiceService } from '../../api';
import { 
  FaSave, 
  FaTimes, 
  FaImage, 
  FaMapMarkerAlt, 
  FaEuroSign,
  FaTag
} from 'react-icons/fa';
import './UserUpdateannonce.css';

const UserUpdateannonce = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    ville: '',
    prix: '',
    type_annonce: 'offre',
    sous_service_id: '',
    image: null,
    remove_image: false // Ajout d'un champ pour supprimer l'image
  });
  
  const [sousServices, setSousServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

useEffect(() => {
  const fetchData = async () => {
    try {
      // Récupérer l'annonce
      const annonceResponse = await annonceService.getOne(id);
      const annonce = annonceResponse.data || annonceResponse;
      
      setFormData({
        titre: annonce.titre || '',
        description: annonce.description || '',
        ville: annonce.ville || '',
        prix: annonce.prix || '',
        type_annonce: annonce.type_annonce || 'offre',
        sous_service_id: annonce.sous_service_id || '',
        image: null,
        remove_image: false
      });

      // Récupérer l'image existante si elle existe
      if (annonce.image) {
        // Stocker l'URL complète de l'image existante
        const imageUrl = annonce.image_url || annonce.image; // Utilisez image_url si disponible
        setImagePreview(imageUrl);
        setExistingImage(imageUrl); // Stocker l'URL complète
      }

      // Récupérer les sous-services
      const sousServicesResponse = await sousServiceService.getAll();
      setSousServices(sousServicesResponse.data || sousServicesResponse || []);

    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [id]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({
        ...prev,
        image: file,
        remove_image: false // Réinitialiser remove_image si on ajoute une nouvelle image
      }));

      // Créer un aperçu de l'image
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  setError(null);
  setSuccess(null);

  try {
    // Préparer les données pour l'envoi
    const submissionData = new FormData();
    
    // Ajouter tous les champs texte
    submissionData.append('titre', formData.titre);
    submissionData.append('description', formData.description);
    submissionData.append('ville', formData.ville);
    submissionData.append('prix', formData.prix);
    submissionData.append('type_annonce', formData.type_annonce);
    submissionData.append('sous_service_id', formData.sous_service_id);
    
    // Ajouter l'image si elle a été modifiée
    if (formData.image instanceof File) {
      submissionData.append('image', formData.image);
    } else if (formData.remove_image) {
      // Si on veut supprimer l'image, envoyer un champ vide
      submissionData.append('image', '');
    }

    // Pour Laravel, utiliser la méthode PUT avec FormData
    submissionData.append('_method', 'PUT');

    console.log('Données envoyées:');
    for (let [key, value] of submissionData.entries()) {
      console.log(key, value);
    }

    // Utiliser axios directement ou votre service modifié
    const response = await annonceService.update(id, submissionData);
    
    // Mettre à jour l'aperçu avec la nouvelle image
    if (response.data && response.data.data) {
      const updatedAnnonce = response.data.data;
      
      if (formData.remove_image) {
        // Si l'image a été supprimée
        setImagePreview(null);
        setExistingImage(null);
      } else if (updatedAnnonce.image_url) {
        // Si une nouvelle image a été uploadée
        setImagePreview(updatedAnnonce.image_url);
        setExistingImage(updatedAnnonce.image_url);
      } else if (updatedAnnonce.image) {
        // Fallback si image_url n'est pas présent
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const fileName = updatedAnnonce.image.split('/').pop();
        const newImageUrl = `${baseUrl}/storage/annonces/${fileName}`;
        setImagePreview(newImageUrl);
        setExistingImage(newImageUrl);
      }
    }
    
    setSuccess('Annonce modifiée avec succès!');
    setTimeout(() => {
      navigate('/mon-compte');
    }, 2000);

  } catch (err) {
    console.error('Erreur détaillée:', err);
    
    // Afficher les erreurs de validation de l'API
    if (err.response && err.response.data && err.response.data.errors) {
      const errors = err.response.data.errors;
      let errorMessage = 'Erreurs de validation: ';
      Object.keys(errors).forEach(key => {
        errorMessage += `${errors[key].join(', ')} `;
      });
      setError(errorMessage);
    } else if (err.response && err.response.data && err.response.data.message) {
      setError(err.response.data.message);
    } else {
      setError(err.message || 'Erreur lors de la modification de l\'annonce');
    }
  } finally {
    setSaving(false);
  }
};

  const handleCancel = () => {
    navigate('/mon-compte');
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      remove_image: true
    }));
    setImagePreview(null);
  };

const resetImage = () => {
  setFormData(prev => ({
    ...prev,
    image: null,
    remove_image: false
  }));
  
  // Restaurer l'image existante avec l'URL complète
  if (existingImage) {
    // Vérifier si l'image existante est déjà une URL complète
    if (existingImage.startsWith('http')) {
      setImagePreview(existingImage);
    } else {
      // Construire l'URL complète
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const imagePath = existingImage.startsWith('storage/') 
        ? existingImage.replace('storage/', '') 
        : existingImage;
      setImagePreview(`${baseUrl}/storage/${imagePath}`);
    }
  }
};

  if (loading) return <div className="loading">Chargement...</div>;

  return (
    <div className="update-annonce-container">
      <div className="update-annonce-card">
        <div className="annonce-header">
          <h1>Modifier l'annonce</h1>
          <p>Mettez à jour les informations de votre annonce</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit} className="update-annonce-form">
          <div className="form-section">
            <h3>Informations principales</h3>
            
            <div className="form-group">
              <label>Titre de l'annonce *</label>
              <input
                type="text"
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                required
                placeholder="Ex: Développeur web expérimenté"
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="5"
                placeholder="Décrivez votre annonce en détail..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ville *</label>
                <div className="input-with-icon">
                  <FaMapMarkerAlt className="input-icon" />
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Paris"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Prix (€) *</label>
                <div className="input-with-icon">
                  <FaEuroSign className="input-icon" />
                  <input
                    type="number"
                    name="prix"
                    value={formData.prix}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type d'annonce *</label>
                <select
                  name="type_annonce"
                  value={formData.type_annonce}
                  onChange={handleChange}
                  required
                >
                  <option value="offre">Offre</option>
                  <option value="demande">Demande</option>
                </select>
              </div>

              <div className="form-group">
                <label>Sous-service *</label>
                <select
                  name="sous_service_id"
                  value={formData.sous_service_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Sélectionnez un sous-service</option>
                  {sousServices.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section Image */}
          <div className="form-section">
            <h3>
              <FaImage className="section-icon" />
              Image de l'annonce
            </h3>
            
            <div className="image-upload-section">
              {imagePreview ? (
                <div className="image-preview-container">
                  <img 
                    src={imagePreview} 
                    alt="Aperçu" 
                    className="image-preview"
                  />
                  <div className="image-actions">
                    <button 
                      type="button" 
                      className="remove-image-btn"
                      onClick={removeImage}
                    >
                      Supprimer
                    </button>
                    {formData.remove_image && (
                      <button 
                        type="button" 
                        className="cancel-remove-btn"
                        onClick={resetImage}
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="image-upload-placeholder">
                  <FaImage className="upload-icon" />
                  <p>Aucune image sélectionnée</p>
                </div>
              )}
              
              <div className="file-input-container">
                <input
                  type="file"
                  id="image"
                  name="image"
                  onChange={handleChange}
                  accept="image/*"
                  className="file-input"
                />
                <label htmlFor="image" className="file-input-label">
                  <FaImage /> {formData.image ? 'Changer l\'image' : 'Choisir une image'}
                </label>
                <p className="file-help">Formats acceptés: JPG, PNG, GIF (Max: 2MB)</p>
                {formData.remove_image && (
                  <p className="warning-text">⚠️ L'image sera supprimée lors de l'enregistrement</p>
                )}
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="cancel-btn"
              disabled={saving}
            >
              <FaTimes /> Annuler
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : <><FaSave /> Enregistrer les modifications</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserUpdateannonce;